import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { getLoyaltyDashboard } from "store/actions/dashboardAction"
import { useTranslation } from "react-i18next"
import { formatWithCommas } from "common/utils"
import { ShimmerThumbnail } from "react-shimmer-effects"
import { Card, CardBody, Col, Row, CardTitle, Table } from "reactstrap"
import PieWithScrollableLegend from "../PieWithScrollableLegend"
import ReactApexChart from "react-apexcharts"
import { useAssignedBrands } from "hooks/useAssignedBrands"
import { hasExternalPosIntegration } from "utils/orderPosUtils"

const LoyaltyPoints = ({ commonDateRange, periodType, date, hasLoyaltyPermission }) => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const dispatch = useDispatch()
  const { loading, loyaltyDashboardData } = useSelector(
    state => state.Dashboard
  )
  const { brands } = useAssignedBrands()
  const hasPos = useMemo(
    () => brands.some(brand => hasExternalPosIntegration(brand)),
    [brands]
  )
  const [loyaltyBreakdownData, setLoyaltyBreakdownData] = useState({
    labels: [],
    datasets: [],
  })
  const [pointsSeries, setPointsSeries] = useState([])
  const [options, setOptions] = useState({
    chart: {
      id: "line-chart",
      toolbar: {
        show: false,
      },
    },
    stroke: {
      width: 2,
      curve: "smooth",
    },
    xaxis: {
      categories: [],
      type: "category",
    },
  })

  const formatDate = date => {
    const year = date.getFullYear()
    const month = (date.getMonth() + 1).toString().padStart(2, "0")
    const day = date.getDate().toString().padStart(2, "0")
    const hours = date?.getHours()?.toString()?.padStart(2, "0")
    const minutes = date?.getMinutes()?.toString()?.padStart(2, "0")
    return `${year}-${month}-${day} ${hours}:${minutes}`
  }

  function getDateOnly(dateTimeString) {
    if (!dateTimeString) return ""
    // Split by space and return the first part (the date)
    const [date] = dateTimeString.split(" ")
    return date
  }

  const generateDateRange = () => {
    if (periodType === "day") {
      return {
        start: formatDate(new Date(date)).split(" ")[0].concat(" 00:00"),
        end: formatDate(new Date(date)).split(" ")[0].concat(" 23:59"),
      }
    }

    if (periodType === "custom") {
      return {
        start: formatDate(new Date(commonDateRange?.start))
          .split(" ")[0]
          .concat(" 00:00"),
        end: formatDate(new Date(commonDateRange?.end))
          .split(" ")[0]
          .concat(" 23:59"),
      }
    }

    return commonDateRange
  }

  const fetchLoyaltyDashboardData = () => {
    // Only fetch if user has Loyalty permission
    if (!hasLoyaltyPermission) return
    
    const payload = {
      dateRange: generateDateRange() || {},
    }

    dispatch(getLoyaltyDashboard(payload))
  }

  useEffect(() => {
    fetchLoyaltyDashboardData()
  }, [commonDateRange, periodType, date])

  // Function to format numbers with exactly 2 decimal places and commas for this component
  const formatWithTwoDecimalsAndCommas = value => {
    const numValue = parseFloat(value) || 0
    return numValue.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  const toTwoDecimalPlaces = value => {
    if (isNaN(value)) return 0
    return Number(parseFloat(value).toFixed(2))
  }

  const loyaltyStats = [
    {
      id: 1,
      title: t("Opening Balance"),
      value: formatWithTwoDecimalsAndCommas(
        loyaltyDashboardData?.openingBalance || 0
      ),
    },
    {
      id: 2,
      title: t("Issued Points"),
      value: formatWithTwoDecimalsAndCommas(
        loyaltyDashboardData?.issuedPoints || 0
      ),
    },
    {
      id: 3,
      title: t("Closing Balance"),
      value: formatWithTwoDecimalsAndCommas(
        loyaltyDashboardData?.closingBalance || 0
      ),
    },
    {
      id: 4,
      title: t("Redeemed Loyalty Points"),
      value: formatWithTwoDecimalsAndCommas(
        loyaltyDashboardData?.redeemedPoints || 0
      ),
    },
    {
      id: 5,
      title: t("Expired Loyalty Points"),
      value: formatWithTwoDecimalsAndCommas(
        loyaltyDashboardData?.expiredPoints || 0
      ),
    },
    {
      id: 6,
      title: t("Revoked Loyalty Points"),
      value: formatWithTwoDecimalsAndCommas(
        loyaltyDashboardData?.revokedPoints +
          loyaltyDashboardData?.revokedByAdmin || 0
      ),
    },
    {
      id: 7,
      title: t("Net Loyalty Points"),
      value: formatWithTwoDecimalsAndCommas(
        Number(
          (loyaltyDashboardData?.closingBalance || 0) -
            loyaltyDashboardData?.openingBalance || 0
        )
      ),
    },
  ]

  useEffect(() => {
    const start =
      loyaltyDashboardData?.summaryForDates?.startDateSummary

    const end =
      loyaltyDashboardData?.summaryForDates?.endDateSummary

    if (!start || !end) return
    
    if(start?.date&&end?.date)
    {
      setOptions({
        chart: { id: "line-chart", toolbar: { show: false } },
        stroke: { width: 4, curve: "smooth" },
        xaxis: {
          categories: [getDateOnly(start?.date), getDateOnly(end?.date)],
          type: "category",
        },
      })
    }

    const formattedSeries = [
      {
        name: t("Issued"),
        data: [
          toTwoDecimalPlaces(start?.pointsIssued),
          toTwoDecimalPlaces(end?.pointsIssued),
        ],
      },
      {
        name: t("Spent"),
        data: [
          toTwoDecimalPlaces(start?.pointsSpent),
          toTwoDecimalPlaces(end?.pointsSpent),
        ],
      },
      {
        name: t("Expired"),
        data: [
          toTwoDecimalPlaces(start?.pointsExpired),
          toTwoDecimalPlaces(end?.pointsExpired),
        ],
      },
      {
        name: t("Available"),
        data: [
          toTwoDecimalPlaces(start?.pointsAvailable),
          toTwoDecimalPlaces(end?.pointsAvailable),
        ],
      },
    ]
    setPointsSeries(formattedSeries)

    const {
      collectedOnOrders,
      collectedAsBonus,
      collectedFromPOS,
      revokedByAdmin,
      redeemedOnOrders,
      expiringAsOfDate,
      expiredAsOnDate,
    } = loyaltyDashboardData

    const breakdownItems = [
      {
        label: t("Collected on orders"),
        value: collectedOnOrders,
        color: "#4C6FFF",
      },
      {
        label: t("Collected as bonus"),
        value: collectedAsBonus,
        color: "#00E396",
      },
    ]

    if (hasPos) {
      breakdownItems.push({
        label: t("Collected from POS"),
        value: collectedFromPOS,
        color: "#FEB019",
      })
    }

    breakdownItems.push(
      {
        label: t("Revoked by admin"),
        value: revokedByAdmin,
        color: "#FF4560",
      },
      {
        label: t("Redeemed on orders"),
        value: redeemedOnOrders,
        color: "#775DD0",
      },
      {
        label: t("Expiring as of the date"),
        value: expiringAsOfDate,
        color: "#00B8FF",
      },
      {
        label: t("Expired as on the date"),
        value: expiredAsOnDate,
        color: "#546E7A",
      }
    )

    setLoyaltyBreakdownData({
      labels: breakdownItems.map(item => item.label),
      datasets: [
        {
          label: t(""),
          data: breakdownItems.map(item => item.value),
          backgroundColor: breakdownItems.map(item => item.color),
        },
      ],
    })
  }, [loyaltyDashboardData, language, t, hasPos])

  if (loading) {
    return (
      <>
        <Row>
          {loyaltyStats?.map(item => (
            <Col key={item.id} lg={4}>
              <Card className="card_100_24">
                <CardBody>
                  <CardTitle className="text-center">{item.title}</CardTitle>
                  <div style={{ direction: "ltr" }}>
                    <ShimmerThumbnail
                      height={30}
                      rounded
                      className="mb-0 pb-0"
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
        {[0, 1, 2, 3, 4, 5, 6, 7].map(i => (
          <Row key={i}>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <ShimmerThumbnail height={400} rounded />
                </CardBody>
              </Card>
            </Col>
          </Row>
        ))}
      </>
    )
  }

  return (
    <>
      <Row>
        {loyaltyStats?.map(item => (
          <Col key={item.id} lg={4}>
            <Card className="card_100_24">
              <CardBody>
                <CardTitle className="text-center">{item.title}</CardTitle>
                <div style={{ direction: "ltr" }}>
                  {loading ? (
                    <ShimmerThumbnail
                      height={30}
                      rounded
                      className="mb-0 pb-0"
                    />
                  ) : (
                    <h4 className="text-center">{item.value}</h4>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      <Row>
        <Col sm={12}>
          <Card>
            <CardBody>
              <CardTitle>{t("Loyalty Points Snapshot")}</CardTitle>
              {loading ? (
                <ShimmerThumbnail height={320} rounded />
              ) : (
                <>
                  <div style={{ direction: "ltr" }}>
                    {pointsSeries.length === 0 && loading ? (
                      "Loading"
                    ) : (
                      <ReactApexChart
                        key={JSON.stringify(pointsSeries)}
                        series={pointsSeries}
                        options={options}
                        type="line"
                        height={320}
                        className="apex-charts"
                      />
                    )}
                  </div>
                </>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col lg={12}>
          <Card>
            <CardBody>
              <div className="table-responsive">
                <Table hover>
                  <thead className="table-light">
                    <tr>
                      <th>{t("Type")}</th>
                      <th>
                        {getDateOnly(
                          loyaltyDashboardData?.summaryForDates
                            ?.startDateSummary?.date
                        )}
                      </th>
                      <th>
                        {getDateOnly(
                          loyaltyDashboardData?.summaryForDates?.endDateSummary
                            ?.date
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{t("Issued")}</td>
                      <td>
                        {formatWithCommas(
                          loyaltyDashboardData?.summaryForDates
                            ?.startDateSummary?.pointsIssued
                        )}
                      </td>
                      <td>
                        {formatWithCommas(
                          loyaltyDashboardData?.summaryForDates?.endDateSummary
                            ?.pointsIssued
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>{t("Spent")}</td>
                      <td>
                        {formatWithCommas(
                          loyaltyDashboardData?.summaryForDates
                            ?.startDateSummary?.pointsSpent
                        )}
                      </td>
                      <td>
                        {formatWithCommas(
                          loyaltyDashboardData?.summaryForDates?.endDateSummary
                            ?.pointsSpent
                        )}
                      </td>
                    </tr>{" "}
                    <tr>
                      <td>{t("Expired")}</td>
                      <td>
                        {formatWithCommas(
                          loyaltyDashboardData?.summaryForDates
                            ?.startDateSummary?.pointsExpired
                        )}
                      </td>
                      <td>
                        {formatWithCommas(
                          loyaltyDashboardData?.summaryForDates?.endDateSummary
                            ?.pointsExpired
                        )}
                      </td>
                    </tr>{" "}
                    <tr>
                      <td>{t("Available")}</td>
                      <td>
                        {formatWithCommas(
                          loyaltyDashboardData?.summaryForDates
                            ?.startDateSummary?.pointsAvailable
                        )}
                      </td>
                      <td>
                        {formatWithCommas(
                          loyaltyDashboardData?.summaryForDates?.endDateSummary
                            ?.pointsAvailable
                        )}
                      </td>
                    </tr>{" "}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="d-flex align-items-stretch">
        <Col lg={6} className="d-flex">
          <Card className="flex-fill">
            <CardBody>
              <div className="table-responsive">
                <Table hover>
                  <thead className="table-light">
                    <tr>
                      <th>{t("Type")}</th>
                      <th>{t("Loyalty Points")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{t("Collected on orders")}</td>
                      <td>
                        {formatWithCommas(
                          loyaltyDashboardData?.collectedOnOrders
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td>{t("Collected as bonus")}</td>
                      <td>
                        {formatWithCommas(
                          loyaltyDashboardData?.collectedAsBonus
                        )}
                      </td>
                    </tr>
                    {hasPos && (
                      <tr>
                        <td>{t("Collected from POS")}</td>
                        <td>
                          {formatWithCommas(
                            loyaltyDashboardData?.collectedFromPOS
                          )}
                        </td>
                      </tr>
                    )}
                    <tr>
                      <td>{t("Revoked by admin")}</td>
                      <td>
                        {formatWithCommas(loyaltyDashboardData?.revokedByAdmin)}
                      </td>
                    </tr>{" "}
                    <tr>
                      <td>{t("Redeemed on orders")}</td>
                      <td>
                        {formatWithCommas(
                          loyaltyDashboardData?.redeemedOnOrders
                        )}
                      </td>
                    </tr>{" "}
                    <tr>
                      <td>{t("Expiring as of the date")}</td>
                      <td>
                        {formatWithCommas(
                          loyaltyDashboardData?.expiringAsOfDate
                        )}
                      </td>
                    </tr>{" "}
                    <tr>
                      <td>{t("Expired as on the date")}</td>
                      <td>
                        {formatWithCommas(
                          loyaltyDashboardData?.expiredAsOnDate
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col lg={6} className="d-flex">
          <Card className="flex-fill">
            <CardBody>
              <CardTitle>{t("Loyalty Points Breakdown")}</CardTitle>
              <div style={{ height: 300, position: "relative" }}>
                <PieWithScrollableLegend
                  data={loyaltyBreakdownData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: true,
                        position: "right",
                      },
                    },
                  }}
                />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default LoyaltyPoints
